import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getEntries, formatAmount, formatDate, formatTime } from '../lib/api';
import { supabase } from '../lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { useNavigate } from 'react-router-dom';

const DayBookPage = () => {
  const { business } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  // IST-aware initial date: new Date().toISOString() returns UTC which is 5h30m
  // behind IST — at 03:00 AM IST it gives yesterday's date. Use IST offset instead.
  const getISTDateString = () => {
    const now = new Date();
    const istOffset = 5.5 * 60 * 60 * 1000;
    const istDate = new Date(now.getTime() + istOffset);
    return istDate.toISOString().split('T')[0];
  };
  const [selectedDate, setSelectedDate] = useState(getISTDateString);
  const pollRef    = useRef(null);
  const channelRef = useRef(null);

  useEffect(() => {
    if (business?.id) {
      loadEntries();
      const cleanup = subscribeToRealtime();
      return cleanup;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business, selectedDate]);

  const loadEntries = async () => {
    try {
      setLoading(true);
      const response = await getEntries(business.id, { date: selectedDate, limit: 100 });
      setEntries(response.data);
    } catch (error) {
      console.error('Error loading entries:', error);
    } finally {
      setLoading(false);
    }
  };

  // Realtime + 5s polling fallback when channel errors (mirrors ChatPage pattern).
  // RLS without an authenticated supabase session will throw CHANNEL_ERROR, so
  // polling kicks in and the user keeps seeing fresh entries.
  const subscribeToRealtime = () => {
    const startPoll = () => {
      if (pollRef.current) return;
      pollRef.current = setInterval(loadEntries, 5000);
    };
    const stopPoll = () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };

    const channel = supabase
      .channel('daybook-entries')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'entries',
        filter: `business_id=eq.${business.id}`
      }, () => {
        loadEntries();
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          stopPoll();
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          startPoll();
        }
      });

    channelRef.current = channel;
    return () => {
      stopPoll();
      try { supabase.removeChannel(channel); }
      catch (err) { console.warn('[DayBook] removeChannel cleanup failed:', err); }
    };
  };

  const getStatusBadge = (status) => {
    const styles = {
      confirmed: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      corroborated: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      disputed: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      voided: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return (
      <Badge variant="outline" className={styles[status] || styles.confirmed}>
        {status}
      </Badge>
    );
  };

  const getEntryTypeIcon = (type) => {
    const icons = {
      trip: '🚛',
      sale: '💰',
      invoice: '📄',
      payment: '✅',
      expense: '💸',
      attendance: '👤'
    };
    return icons[type] || '📝';
  };

  // Group entries by date
  const groupedEntries = entries.reduce((acc, entry) => {
    const date = formatDate(entry.entry_date);
    if (!acc[date]) acc[date] = [];
    acc[date].push(entry);
    return acc;
  }, {});

  return (
    <div className="min-h-screen text-white pb-20" data-testid="daybook-page" style={{ background: '#000', fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif" }}>
      {/* Header */}
      <header className="p-4 sticky top-0 z-10" style={{ background: 'rgba(0,0,0,.85)', backdropFilter: 'saturate(180%) blur(20px)', borderBottom: '1px solid #1C1C1E' }}>
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold">Day Book</h1>
            <p className="text-sm" style={{ color: '#8E8E93' }}>Transaction Ledger</p>
          </div>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="rounded-xl px-3 py-1 text-sm text-white border-0"
            style={{ background: '#2C2C2E' }}
            data-testid="date-picker"
          />
        </div>
      </header>

      <ScrollArea className="h-[calc(100vh-140px)]">
        <main className="p-4 space-y-4" data-testid="entries-list">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'transparent transparent #FF2D55 transparent' }}></div>
            </div>
          ) : entries.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#636366' }}>
              <p className="font-medium" style={{ color: '#8E8E93' }}>
                No entries for {selectedDate}
              </p>
              <p className="text-sm mt-1" style={{ color: '#636366' }}>
                Send a WhatsApp message to record your first entry.
              </p>
            </div>
          ) : (
            Object.entries(groupedEntries).map(([date, dateEntries]) => (
              <div key={date} className="space-y-2">
                <h3 className="text-xs font-medium px-2" style={{ color: '#8E8E93' }}>{date}</h3>
                {dateEntries.map((entry) => (
                  <div
                    key={entry.id}
                    className={`rounded-2xl p-4 ${entry.status === 'voided' ? 'opacity-60' : ''}`}
                    style={{ background: '#1C1C1E', border: '1px solid #2C2C2E' }}
                    data-testid={`entry-card-${entry.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">{getEntryTypeIcon(entry.entry_type)}</span>
                        <div>
                          <p className={`font-medium ${entry.status === 'voided' ? 'line-through' : 'text-white'}`}
                            style={entry.status === 'voided' ? { color: '#636366', textDecoration: 'line-through' } : {}}>
                            {entry.party_name || 'Unknown Party'}
                          </p>
                          <p className={`text-sm mt-0.5 ${entry.status === 'voided' ? 'line-through' : ''}`}
                            style={{ color: entry.status === 'voided' ? '#48484A' : '#8E8E93' }}>
                            {entry.quantity && `${entry.quantity} ${entry.unit || ''} `}
                            {entry.item_name || entry.entry_type}
                          </p>
                          <div className="flex items-center gap-2 mt-1.5">
                            <span className="text-xs" style={{ color: '#636366' }}>{formatTime(entry.entry_date)}</span>
                            {getStatusBadge(entry.status)}
                            {entry.entry_code && (
                              <span className="text-xs font-mono px-1.5 py-0.5 rounded"
                                style={{ background: '#2C2C2E', color: '#636366', letterSpacing: '0.02em' }}
                                data-testid={`entry-code-${entry.id}`}>
                                {entry.entry_code}
                              </span>
                            )}
                            {entry.correction_of && (
                              <Badge variant="outline" className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs">
                                Correction of #{entry.correction_of}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-bold ${
                          entry.status === 'voided' ? 'line-through' :
                          entry.party_role === 'supplier' ? 'text-blue-400' : 'text-emerald-400'
                        }`} style={entry.status === 'voided' ? { color: '#636366' } : {}}>
                          {/* party_role=supplier → outflow (−); customer/employee/null → inflow (+)
                              Fallback for null party_role: use entry_type for legacy entries */}
                          {['stock_deduct', 'stock_add', 'stock'].includes(
                            entry.entry_type) ? (
                            <span>
                              {entry.entry_type === 'stock_add'
                                ? '(+)' : '(-)'}
                              {entry.quantity
                                ? `${entry.quantity} ${entry.unit || 'nos'}`
                                : '—'}
                            </span>
                          ) : (
                            <span>
                              {(entry.party_role === 'supplier' ||
                                (!entry.party_role &&
                                  entry.entry_type === 'payment'))
                                ? '(-)' : '(+)'}
                              {formatAmount(entry.total_amount)}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    {entry.initiated_by && (
                      <div className="mt-2 pt-2" style={{ borderTop: '1px solid #2C2C2E' }}>
                        <p className="text-xs" style={{ color: '#636366' }}>
                          By {entry.initiated_by.name || 'Unknown'} ({entry.initiated_by.role || 'user'})
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))
          )}
        </main>
      </ScrollArea>

      {/* Summary Footer */}
      <div className="fixed bottom-16 left-0 right-0 p-3" style={{ background: '#1C1C1E', borderTop: '1px solid #2C2C2E' }}>
        <div className="flex justify-around text-center">
          <div>
            <p className="text-xs" style={{ color: '#8E8E93' }}>In</p>
            <p className="font-bold text-emerald-400">
              {formatAmount(entries.filter(e => ['confirmed', 'corroborated'].includes(e.status) && e.party_role !== 'supplier').reduce((sum, e) => sum + (e.total_amount || 0), 0))}
            </p>
          </div>
          <div>
            <p className="text-xs" style={{ color: '#8E8E93' }}>Out</p>
            <p className="font-bold text-blue-400">
              {formatAmount(entries.filter(e => ['confirmed', 'corroborated'].includes(e.status) && e.party_role === 'supplier').reduce((sum, e) => sum + (e.total_amount || 0), 0))}
            </p>
          </div>
          <div>
            <p className="text-xs" style={{ color: '#8E8E93' }}>Entries</p>
            <p className="font-bold text-white">{entries.filter(e => ['confirmed', 'corroborated'].includes(e.status)).length}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DayBookPage;
